import React from 'react';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { PaymentRequest } from '@stripe/stripe-js';
import { OrderPaymentType } from 'components/order/model/Order';
import { EPaymentProvider, isPaymentProvider } from 'components/settings/model/Settings';
import { ApplicationState } from 'store/store';
import { LoadingButton } from 'ui/LoadingButton';

const StyledBox = styled(Box)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1)
}));

interface IProps {
    walletType: OrderPaymentType.APPLEPAY | OrderPaymentType.GOOGLEPAY;
    id: string;
    disabled?: boolean;
    hidden?: boolean;
    onClick?: (walletType: IProps['walletType']) => void;
    stripePaymentRequest?: PaymentRequest | null;
}

export const DigitalWalletButtonLegacy: React.FC<React.PropsWithChildren<IProps>> = ({
    walletType,
    disabled,
    id,
    onClick,
    hidden,
    children,
    stripePaymentRequest
}) => {
    const { settings } = useSelector((state: ApplicationState) => state.settings);

    const isStripe = React.useMemo(
        () =>
            !!settings &&
            isPaymentProvider(settings.paymentProvider) &&
            settings.paymentProvider === EPaymentProvider.STRIPE &&
            !!settings.stripe.publishableKey,
        [settings]
    );

    const handleClick = React.useCallback(() => {
        if (isStripe && stripePaymentRequest) {
            return stripePaymentRequest.show();
        }
        if (onClick) {
            onClick(walletType);
        }
    }, [isStripe, onClick, stripePaymentRequest, walletType]);

    return (
        <StyledBox boxShadow={1}>
            <LoadingButton
                variant="contained"
                color="primary"
                id={id}
                fullWidth
                disabled={disabled || hidden}
                onClick={handleClick}
            >
                {children}
            </LoadingButton>
        </StyledBox>
    );
};
